import { Box, Center, useDisclosure } from "@chakra-ui/react";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AcceptTermsAndConds } from "../../components/AcceptTermsAndConds/AcceptTermpsAndConds";
import { ConfirmUsePhoto } from "../../components/ConfirmUsePhoto/ConfirmUsePhoto";
import { GLAMLabHeader } from "../../components/GLAMLabHeader/GLAMLabHeader";
import { Liqa } from "../../components/Liqa/Liqa";
import { SelfieSelector } from "../../components/SelfieSelector/SelfieSelector";
import TermsAndConsAlertDialog from "../../components/TermsAndCondsAlertDialog/TermsAndCondsAlertDialog";
import useScreen from "../../hooks/useScreen";
import useSession from "../../hooks/useSession";
import { useSourcePhoto } from "../../hooks/useSourcePhoto";
import useTipsAndTerms from "../../hooks/useTipsAndTerms";
import { State } from "../../interfaces/State";
import { Channel } from "../../interfaces/tracking";
import type { AppDispatch } from "../../reducers/configureStore";
import {
  deleteSelfie,
  setCapture,
  setIsUpload,
} from "../../reducers/selfie/selfieSlice";
import { setSelectedTransfer } from "../../reducers/style/styleSlice";
import {
  setAppliedImage,
  setTransfers,
} from "../../reducers/volume/volumeSlice";
import { BACKGROUND_IMAGE } from "../../utils/constants";
import { Analytics, Event, EventNames } from "../../utils/tracking";

export interface TakePhotoPageProps {
  from: string;
}

const pages: { [key: string]: { text: string; vto: string } } = {
  color: {
    text: "Hair Color Try-On",
    vto: "color",
  },
};

interface TakePhotoState {
  terms: ITerms;
  selfie: ISelfie;
}
interface ITerms {
  acceptTerms: boolean;
}

interface ISelfie {
  selfie: any;
}

export const TakePhotoPage: FC<TakePhotoPageProps> = (props) => {
  const { isMobile } = useScreen();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [gotItRecommendations, setGotItRecommendations] =
    useState<boolean>(false);
  const [disableBtnGallery, setDisableBtnGallery] = useState<boolean>(true);
  const [disableBtnTakePhoto, setdisableBtnTakePhoto] = useState<boolean>(true);
  const [showLIQATips, setShowLIQATips] = useState<boolean>(false);
  const { acceptTerms } = useSelector((state: TakePhotoState) => state.terms);
  const { allSelfies, capture, isUpload } = useSelector(
    (state: State) => state.selfie
  );
  const { currentUser, isLoggedIn } = useSession();
  const [captured, setCaptured] = useState<string | ArrayBuffer | null>(
    capture
  );
  const [pendingEvents, setPendingEvents] = useState<Event[]>([]);
  const [image, setImage] = useState<string | ArrayBuffer | null>(null);
  const sourcePhoto = useSourcePhoto();

  const [showTips, setShowTips] = useState<boolean>(!captured);
  const location = useLocation();
  const redirectTo: string =
    new URLSearchParams(location.search).get("try-on") ?? "";
  const platform: string =
    new URLSearchParams(location.search).get("platform") ?? "";

  const paths = [{ text: pages[redirectTo]?.text ?? "Hair Color Try-On", url: "" }];

  const {
    isOpen: isOpenTdC,
    onOpen: onOpenTdC,
    onClose: onCloseTdC,
  } = useDisclosure();

  const cameraContainerRef = useRef<any>();
  const containerRef = useRef<any>();
  const ref = useRef<any>();

  useEffect(() => {
    window.scrollTo(0, 0);
    !capture && sessionStorage.removeItem("vtoSelfieId");

    let liqa = document?.querySelector("hautai-liqa");
    const interval = setInterval(() => {
      if (liqa) {
        clearInterval(interval);
        liqa?.addEventListener("capture", onClickUsePhoto);
        return;
      }

      liqa = document?.querySelector("hautai-liqa");
    }, 100);

    dispatch(setSelectedTransfer(undefined));
  }, []);

  useTipsAndTerms({
    acceptTerms,
    isOpenTdC,
    showTips,
    onOpenTdC,
    setShowTips,
  });

  const onClickTakePhoto = async () => {
    sessionStorage.removeItem("vtoSelfieId");
    setDisableBtnGallery(true);
    setdisableBtnTakePhoto(true);
    dispatch(setIsUpload(false));
    Analytics.event(Channel.GA, EventNames.PHOTO_TAKEN, {
      login: isLoggedIn(),
      device: platform.includes("g-mob")
        ? "app"
        : isMobile
        ? "mobile"
        : "desktop",
      option: redirectTo,
      photo_saved: allSelfies?.length ? "yes" : "no",
    });
    dispatch(setSelectedTransfer(undefined));
    if (!showTips && !captured) {
      setShowLIQATips(false);
    }
  };

  const onClickGotItRecommendations = async () => {
    setGotItRecommendations(true);
    setDisableBtnGallery(false);
    setdisableBtnTakePhoto(false);
    if (!acceptTerms) {
      onOpenTdC();
      return;
    }
  };

  function blobToBase64(blob: Blob) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }

  const onClickUsePhoto = async (event?: any) => {
    try {
      let photo = captured;
      if (event) {
        let capture = event?.detail;
        capture = capture.transform({ horizontalFlip: true });
        const blob = await capture.blob(); // returns the captured image as Blob JPEG
        photo = (await blobToBase64(blob)) as string;
      }

      dispatch(setSelectedTransfer(undefined));
      dispatch(setCapture(photo));

      const time = new Date();
      sessionStorage.setItem("takePhotoAt", time.toLocaleString("en-US"));
      Analytics.event(Channel.GA, EventNames.PHOTO_CONFIRMATION_LOADED, {
        login: isLoggedIn(),
        device: platform.includes("g-mob")
          ? "app"
          : isMobile
          ? "mobile"
          : "desktop",
        user_id: currentUser?.gti,
        option: redirectTo,
        photo_saved: allSelfies?.length ? "yes" : "no",
        photos: allSelfies?.length,
        source: platform.includes("g-mob")
          ? "native"
          : isMobile
          ? "mobile"
          : "desktop",
      });
      const store = new URLSearchParams(location.search).get("store");
      navigate({
        search: `page=vto-${redirectTo}${
          platform.includes("g-mob") ? "&platform=g-mob" : ""
        }${store ? "&store=" + store : ""}`,
      });
    } catch (error: any) {
      console.log("error: ", error);
    }
  };

  const onClickRetakePhoto = () => {
    dispatch(setTransfers([]));

    dispatch(setAppliedImage(""));
    dispatch(setIsUpload(false));
    dispatch(deleteSelfie());
    sessionStorage.removeItem("vtoSelfieId");
    setCaptured(null);
    dispatch(setSelectedTransfer(undefined));

    setImage(null);
    setShowLIQATips(true);
    setDisableBtnGallery(false);
    setdisableBtnTakePhoto(false);
  };

  const TermsAndCondsNotAccepted = () => {
    Analytics.event(Channel.GA, EventNames.LEAGAL_ALERT_LOADED, {
      login: isLoggedIn(),
      device: platform.includes("g-mob")
        ? "app"
        : isMobile
        ? "mobile"
        : "desktop",
      user_id: currentUser?.gti,
      option: paths[0].text,
      photo_saved: allSelfies?.length ? "yes" : "no",
      photos: allSelfies?.length,
    });
    setDisableBtnGallery(false);
  };

  useEffect(() => {
    window?.utag?.view({
      page_name: "hair color try on",
      page_type: "virtual try-on",
      page_group: "virtual try-on",
    });
  }, []);

  useEffect(() => {
    let event: Event = {
      channel: Channel.ALL,
      name: EventNames.CAMERA_LOADED,
      data: undefined,
    };
    if (acceptTerms) {
      setDisableBtnGallery(false);
      setdisableBtnTakePhoto(false);
    }
    if (event.data) {
      if (isLoggedIn() === "yes" && allSelfies === undefined) {
        setPendingEvents((prev) => [...prev, event]);
      } else {
        Analytics.event(event.channel, event.name, event.data);
      }
    }
  }, [acceptTerms]);

  useEffect(() => {
    if (acceptTerms) {
      const liqa = document?.querySelector("hautai-liqa");
      liqa?.addEventListener("capture", onClickUsePhoto);
    }
  }, [acceptTerms]);

  useEffect(() => {
    pendingEvents.forEach((event) => {
      if (Object.hasOwn(event.data, "photos")) {
        event.data.photo = allSelfies?.length ? "yes" : "no";
        event.data.photos = allSelfies?.length ?? 0;
      }

      Analytics.event(event.channel, event.name, event.data);
    });
    setPendingEvents([]);
  }, [allSelfies]);

  useEffect(() => {
    if (isOpenTdC) {
      Analytics.event(Channel.GA, EventNames.LEGAL_LOADED, {
        login: isLoggedIn(),
        device: platform.includes("g-mob")
          ? "app"
          : isMobile
          ? "mobile"
          : "desktop",
        user_id: currentUser?.gti,
        option: paths[0].text,
        photo_saved: allSelfies?.length ? "yes" : "no",
        photos: allSelfies?.length,
      });
    }
  }, [isOpenTdC]);

  const onChange = (image: string | ArrayBuffer | null) => {
    Analytics.event(Channel.GA, EventNames.UPLOAD_SELECTED, {
      login: isLoggedIn(),
      option: redirectTo,
    });
    dispatch(setIsUpload(true));
    setDisableBtnGallery(true);
    setdisableBtnTakePhoto(true);
    setImage(image);
    setCaptured(image);
    setShowLIQATips(false);
  };

  return (
    <Fragment>
      <Box
        minH="100vh"
        bgImage={{
          base: "none",
          md: process.env.PUBLIC_URL + BACKGROUND_IMAGE,
        }}
        bgSize="cover"
      >
        <Box
          position={"relative"}
          display="flex"
          flexDirection="column"
          alignItems={{ base: "inherit", md: "center" }}
        >
          {isMobile && (
            <Box
              h="56px"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box fontSize="16px" fontWeight="bold" color="#000000">
                {paths[0].text}
              </Box>
            </Box>
          )}

          {!isMobile ? <GLAMLabHeader paths={paths} /> : null}

          {/* container */}

          <Box
            bg="#FFFFFF"
            borderLeft={{ md: "0.0625rem solid #E6E6E6" }}
            borderRight={{ md: "0.0625rem solid #E6E6E6" }}
            borderBottom={{ md: "0.0625rem solid #E6E6E6" }}
            w={{ base: "100%", md: "46.3125rem" }}
            minH={
              containerRef.current?.clientWidth -
              (isMobile ? 0 : containerRef.current?.clientWidth * 0.08)
            }
            pt={{
              md:
                (showLIQATips && !showTips) || (!isMobile && showTips)
                  ? 0
                  : "1.875rem",
            }}
            maxWidth={{ base: "100%", md: "47.4375rem" }}
            display={{ base: "block", md: "flex" }}
            flexDirection={{ md: "column" }}
            alignItems="center"
            ref={containerRef}
            position="relative"
          >
            {!isMobile && captured && (
              <ConfirmUsePhoto
                onClickUsePhoto={onClickUsePhoto}
                onClickRetakePhoto={onClickRetakePhoto}
                isMobile={isMobile}
                redirectTo={redirectTo}
              />
            )}
            <Box
                key={1}
                position="relative"
                ref={cameraContainerRef}
                minW={{ md: showTips ? "43.75rem" : "auto" }}
                w={{ md: showTips ? "100%" : "auto" }}
                overflow={{ md: "hidden" }}
                minH={"inherit"}
                mb={{ md: "5rem" }}
              >
                {captured && (
                  <Center>
                    <Box
                      background={`linear-gradient(0deg, rgba(0, 0, 0, 0.47) 0%, rgba(0, 0, 0, 0.47) 100%), url(${captured})`}
                      height={isMobile ? "100vw" : "625px"}
                      width={isMobile ? "100%" : "625px"}
                      backgroundSize="cover"
                      backgroundPosition="center"
                      backgroundRepeat="no-repeat"
                      alignContent={"end"}
                    >
                      {captured && isMobile && (
                        <ConfirmUsePhoto
                          onClickUsePhoto={onClickUsePhoto}
                          onClickRetakePhoto={onClickRetakePhoto}
                          isMobile={isMobile}
                          redirectTo={redirectTo}
                        />
                      )}
                    </Box>
                  </Center>
                )}
                <div>
                    {!acceptTerms && (
                      <Box
                        bg={{
                          base: acceptTerms
                            ? showTips
                              ? "rgb(0,0,0, 0.33)"
                              : "rgb(0,0,0, 0)"
                            : `rgb(253,241,243,1)`,
                          md: "transparent",
                        }}
                        bgImage={
                          !acceptTerms
                            ? `url(${process.env.PUBLIC_URL}/assets/images/texture-ultafetti.png)`
                            : "none"
                        }
                        bgSize={{ base: 950 }}
                        w={"100%"}
                        h={"100%"}
                        position={{ base: "absolute" }}
                        top="0"
                        textAlign="center"
                        alignItems={captured ? "flex-end" : "center"}
                        py={{ md: captured ? "1.25rem" : "2.1875rem" }}
                        display={!acceptTerms ? "flex" : "none"}
                        zIndex="2"
                      >
                        {!acceptTerms && !captured && (
                          <AcceptTermsAndConds onOpenTdC={onOpenTdC} />
                        )}
                        {isMobile && captured && (
                          <ConfirmUsePhoto
                            onClickUsePhoto={onClickUsePhoto}
                            onClickRetakePhoto={onClickRetakePhoto}
                            isMobile={isMobile}
                            redirectTo={redirectTo}
                          />
                        )}
                      </Box>
                    )}

                    {acceptTerms && (
                      <Box
                        id="liqa-container-new"
                        ref={ref}
                        m="0 auto"
                        mt={isMobile ? "0" : acceptTerms ? "5rem" : "0"}
                        display={captured ? "none" : "flex"}
                        h={isMobile ? "152vw" : "39.0625rem"}
                        w={isMobile ? "90vw" : "39.0625rem"}
                      >
                        <Liqa
                          hidden={captured ? true : undefined}
                          isMobile={isMobile}
                        />
                      </Box>
                    )}
                </div>
              </Box>
            {acceptTerms ? <SelfieSelector screen={paths[0].text} /> : null}
          </Box>
        </Box>

        <TermsAndConsAlertDialog
          isOpenTdC={isOpenTdC}
          onCloseTdC={onCloseTdC}
          onDeclineTdC={TermsAndCondsNotAccepted}
          screen={paths[0].text}
        />
      </Box>
    </Fragment>
  );
};
